import type { NamespaceCatalog } from '@/i18n/ns';

export type NativePostgresKey =
  | 'manageTitle'
  | 'connectTitle'
  | 'chooseTablesTitle'
  | 'chooseAccessTitle'
  | 'connectedTitle'
  | 'savedTitle'
  | 'readOnlyReplica'
  | 'readOnlyAccount'
  | 'tablesReachOne'
  | 'tablesReachMany'
  | 'tlsReplica'
  | 'tlsAccount'
  | 'tlsChecked'
  | 'tablesSelected'
  | 'tablesInSchema'
  | 'reconnect';

const catalog = {
  en: {
    manageTitle: 'Manage PostgreSQL',
    connectTitle: 'Connect PostgreSQL',
    chooseTablesTitle: 'Choose tables',
    chooseAccessTitle: 'Choose database access',
    connectedTitle: 'PostgreSQL connected',
    savedTitle: 'PostgreSQL saved',
    readOnlyReplica: 'This database is a read-only replica.',
    readOnlyAccount: 'The selected tables do not support row changes with this database account.',
    tablesReachOne: 'What it can reach · {count} table',
    tablesReachMany: 'What it can reach · {count} tables',
    tlsReplica: 'Read-only replica',
    tlsAccount: 'Read-only account',
    tlsChecked: 'Database permissions checked',
    tablesSelected: '{count} of {total} tables selected',
    tablesInSchema: '{count} / {total} tables',
    reconnect: 'Reconnect',
  },
  bg: {
    manageTitle: 'Управление на PostgreSQL',
    connectTitle: 'Свързване с PostgreSQL',
    chooseTablesTitle: 'Избор на таблици',
    chooseAccessTitle: 'Избор на достъп до базата данни',
    connectedTitle: 'PostgreSQL е свързан',
    savedTitle: 'PostgreSQL е запазен',
    readOnlyReplica: 'Тази база данни е реплика само за четене.',
    readOnlyAccount:
      'Избраните таблици не поддържат промени по редове с този акаунт за базата данни.',
    tablesReachOne: 'Достъпни таблици · {count} таблица',
    tablesReachMany: 'Достъпни таблици · {count} таблици',
    tlsReplica: 'Реплика само за четене',
    tlsAccount: 'Акаунт само за четене',
    tlsChecked: 'Разрешенията за базата данни са проверени',
    tablesSelected: 'Избрани са {count} от {total} таблици',
    tablesInSchema: '{count} / {total} таблици',
    reconnect: 'Свързване отново',
  },
  hr: {
    manageTitle: 'Upravljanje PostgreSQL-om',
    connectTitle: 'Povezivanje s PostgreSQL-om',
    chooseTablesTitle: 'Odaberite tablice',
    chooseAccessTitle: 'Odaberite pristup bazi podataka',
    connectedTitle: 'PostgreSQL je povezan',
    savedTitle: 'PostgreSQL je spremljen',
    readOnlyReplica: 'Ova je baza podataka replika samo za čitanje.',
    readOnlyAccount: 'Odabrane tablice ne podržavaju promjene redaka s ovim računom baze podataka.',
    tablesReachOne: 'Dostupno · {count} tablica',
    tablesReachMany: 'Dostupno · {count} tablica',
    tlsReplica: 'Replika samo za čitanje',
    tlsAccount: 'Račun samo za čitanje',
    tlsChecked: 'Dopuštenja baze podataka provjerena',
    tablesSelected: 'Odabrano je {count} od {total} tablica',
    tablesInSchema: '{count} / {total} tablica',
    reconnect: 'Ponovno se poveži',
  },
  cs: {
    manageTitle: 'Spravovat PostgreSQL',
    connectTitle: 'Připojit PostgreSQL',
    chooseTablesTitle: 'Vybrat tabulky',
    chooseAccessTitle: 'Vybrat přístup k databázi',
    connectedTitle: 'PostgreSQL připojeno',
    savedTitle: 'PostgreSQL uloženo',
    readOnlyReplica: 'Tato databáze je replikou pouze pro čtení.',
    readOnlyAccount: 'Vybrané tabulky nepodporují změny řádků s tímto účtem databáze.',
    tablesReachOne: 'Co je dostupné · {count} tabulka',
    tablesReachMany: 'Co je dostupné · {count} tabulek',
    tlsReplica: 'Replika pouze pro čtení',
    tlsAccount: 'Účet pouze pro čtení',
    tlsChecked: 'Oprávnění k databázi zkontrolována',
    tablesSelected: 'Vybráno {count} z {total} tabulek',
    tablesInSchema: '{count} / {total} tabulek',
    reconnect: 'Znovu připojit',
  },
  da: {
    manageTitle: 'PostgreSQL verwalten',
    connectTitle: 'PostgreSQL verbinden',
    chooseTablesTitle: 'Tabellen auswählen',
    chooseAccessTitle: 'Datenbankzugriff auswählen',
    connectedTitle: 'PostgreSQL verbunden',
    savedTitle: 'PostgreSQL gespeichert',
    readOnlyReplica: 'Diese Datenbank ist ein schreibgeschütztes Replikat.',
    readOnlyAccount:
      'Die ausgewählten Tabellen unterstützen mit diesem Datenbankkonto keine Zeilenänderungen.',
    tablesReachOne: 'Erreichbar · {count} Tabelle',
    tablesReachMany: 'Erreichbar · {count} Tabellen',
    tlsReplica: 'Schreibgeschütztes Replikat',
    tlsAccount: 'Schreibgeschütztes Konto',
    tlsChecked: 'Datenbankberechtigungen geprüft',
    tablesSelected: '{count} von {total} Tabellen ausgewählt',
    tablesInSchema: '{count} / {total} Tabellen',
    reconnect: 'Erneut verbinden',
  },
  nl: {
    manageTitle: 'PostgreSQL beheren',
    connectTitle: 'Verbinden met PostgreSQL',
    chooseTablesTitle: 'Tabellen kiezen',
    chooseAccessTitle: 'Databasetoegang kiezen',
    connectedTitle: 'PostgreSQL verbonden',
    savedTitle: 'PostgreSQL opgeslagen',
    readOnlyReplica: 'Deze database is een alleen-lezenreplica.',
    readOnlyAccount:
      'De geselecteerde tabellen ondersteunen geen wijzigingen aan rijen met dit databaseaccount.',
    tablesReachOne: 'Bereik · {count} tabel',
    tablesReachMany: 'Bereik · {count} tabellen',
    tlsReplica: 'Alleen-lezenreplica',
    tlsAccount: 'Alleen-lezenaccount',
    tlsChecked: 'Databasemachtigingen gecontroleerd',
    tablesSelected: '{count} van {total} tabellen geselecteerd',
    tablesInSchema: '{count} / {total} tabellen',
    reconnect: 'Opnieuw verbinden',
  },
  et: {
    manageTitle: 'Halda PostgreSQL-i',
    connectTitle: 'Ühenda PostgreSQL',
    chooseTablesTitle: 'Vali tabelid',
    chooseAccessTitle: 'Vali andmebaasi juurdepääs',
    connectedTitle: 'PostgreSQL on ühendatud',
    savedTitle: 'PostgreSQL on salvestatud',
    readOnlyReplica: 'See andmebaas on kirjutuskaitstud koopia.',
    readOnlyAccount: 'Valitud tabelite ridade muutmine pole selle andmebaasikontoga toetatud.',
    tablesReachOne: 'Ligipääsetav · {count} tabel',
    tablesReachMany: 'Ligipääsetav · {count} tabelit',
    tlsReplica: 'Kirjutuskaitstud koopia',
    tlsAccount: 'Kirjutuskaitstud konto',
    tlsChecked: 'Andmebaasiõigused on kontrollitud',
    tablesSelected: 'Valitud on {count} tabelit {total}-st',
    tablesInSchema: '{count} / {total} tabelit',
    reconnect: 'Ühenda uuesti',
  },
  fi: {
    manageTitle: 'Hallitse PostgreSQL-yhteyttä',
    connectTitle: 'Yhdistä PostgreSQL:ään',
    chooseTablesTitle: 'Valitse taulut',
    chooseAccessTitle: 'Valitse tietokannan käyttöoikeudet',
    connectedTitle: 'PostgreSQL yhdistetty',
    savedTitle: 'PostgreSQL tallennettu',
    readOnlyReplica: 'Tämä tietokanta on vain luku -replika.',
    readOnlyAccount: 'Valitut taulut eivät tue rivien muutoksia tällä tietokantatilillä.',
    tablesReachOne: 'Käytettävissä · {count} taulu',
    tablesReachMany: 'Käytettävissä · {count} taulua',
    tlsReplica: 'Vain luku -replika',
    tlsAccount: 'Vain luku -tili',
    tlsChecked: 'Tietokannan käyttöoikeudet tarkistettu',
    tablesSelected: '{count}/{total} taulua valittu',
    tablesInSchema: '{count}/{total} taulua',
    reconnect: 'Yhdistä uudelleen',
  },
  fr: {
    manageTitle: 'Gérer PostgreSQL',
    connectTitle: 'Se connecter à PostgreSQL',
    chooseTablesTitle: 'Choisir les tables',
    chooseAccessTitle: 'Choisir l’accès à la base de données',
    connectedTitle: 'PostgreSQL connecté',
    savedTitle: 'PostgreSQL enregistré',
    readOnlyReplica: 'Cette base de données est un réplica en lecture seule.',
    readOnlyAccount:
      'Les tables sélectionnées ne prennent pas en charge les modifications de lignes avec ce compte de base de données.',
    tablesReachOne: 'Accès possible · {count} table',
    tablesReachMany: 'Accès possible · {count} tables',
    tlsReplica: 'Réplica en lecture seule',
    tlsAccount: 'Compte en lecture seule',
    tlsChecked: 'Autorisations de la base de données vérifiées',
    tablesSelected: '{count} tables sur {total} sélectionnées',
    tablesInSchema: '{count} / {total} tables',
    reconnect: 'Se reconnecter',
  },
  de: {
    manageTitle: 'PostgreSQL verwalten',
    connectTitle: 'PostgreSQL verbinden',
    chooseTablesTitle: 'Tabellen auswählen',
    chooseAccessTitle: 'Datenbankzugriff auswählen',
    connectedTitle: 'PostgreSQL verbunden',
    savedTitle: 'PostgreSQL gespeichert',
    readOnlyReplica: 'Diese Datenbank ist ein schreibgeschütztes Replikat.',
    readOnlyAccount:
      'Die ausgewählten Tabellen unterstützen mit diesem Datenbankkonto keine Zeilenänderungen.',
    tablesReachOne: 'Erreichbar · {count} Tabelle',
    tablesReachMany: 'Erreichbar · {count} Tabellen',
    tlsReplica: 'Schreibgeschütztes Replikat',
    tlsAccount: 'Schreibgeschütztes Konto',
    tlsChecked: 'Datenbankberechtigungen geprüft',
    tablesSelected: '{count} von {total} Tabellen ausgewählt',
    tablesInSchema: '{count} / {total} Tabellen',
    reconnect: 'Erneut verbinden',
  },
  el: {
    manageTitle: 'Διαχείριση PostgreSQL',
    connectTitle: 'Σύνδεση PostgreSQL',
    chooseTablesTitle: 'Επιλογή πινάκων',
    chooseAccessTitle: 'Επιλογή πρόσβασης στη βάση δεδομένων',
    connectedTitle: 'Το PostgreSQL συνδέθηκε',
    savedTitle: 'Το PostgreSQL αποθηκεύτηκε',
    readOnlyReplica: 'Αυτή η βάση δεδομένων είναι αντίγραφο μόνο για ανάγνωση.',
    readOnlyAccount:
      'Οι επιλεγμένοι πίνακες δεν υποστηρίζουν αλλαγές γραμμών με αυτόν τον λογαριασμό βάσης δεδομένων.',
    tablesReachOne: 'Πίνακες στους οποίους μπορεί να έχει πρόσβαση · {count} πίνακας',
    tablesReachMany: 'Πίνακες στους οποίους μπορεί να έχει πρόσβαση · {count} πίνακες',
    tlsReplica: 'Αντίγραφο μόνο για ανάγνωση',
    tlsAccount: 'Λογαριασμός μόνο για ανάγνωση',
    tlsChecked: 'Τα δικαιώματα της βάσης δεδομένων ελέγχθηκαν',
    tablesSelected: 'Επιλέχθηκαν {count} από τους {total} πίνακες',
    tablesInSchema: '{count} / {total} πίνακες',
    reconnect: 'Επανασύνδεση',
  },
  hu: {
    manageTitle: 'PostgreSQL kezelése',
    connectTitle: 'Csatlakozás a PostgreSQL-hez',
    chooseTablesTitle: 'Táblák kiválasztása',
    chooseAccessTitle: 'Adatbázis-hozzáférés kiválasztása',
    connectedTitle: 'Csatlakoztatva a PostgreSQL-hez',
    savedTitle: 'PostgreSQL mentve',
    readOnlyReplica: 'Ez az adatbázis csak olvasható replika.',
    readOnlyAccount:
      'A kiválasztott táblák sorainak módosítása nem támogatott ezzel az adatbázis-fiókkal.',
    tablesReachOne: 'Elérhető táblák · {count} tábla',
    tablesReachMany: 'Elérhető táblák · {count} tábla',
    tlsReplica: 'Csak olvasható replika',
    tlsAccount: 'Csak olvasható fiók',
    tlsChecked: 'Adatbázis-engedélyek ellenőrizve',
    tablesSelected: '{count} / {total} tábla kiválasztva',
    tablesInSchema: '{count} / {total} tábla',
    reconnect: 'Újracsatlakozás',
  },
  ga: {
    manageTitle: 'Bainistigh PostgreSQL',
    connectTitle: 'Ceangail le PostgreSQL',
    chooseTablesTitle: 'Roghnaigh táblaí',
    chooseAccessTitle: 'Roghnaigh rochtain ar an mbunachar sonraí',
    connectedTitle: 'Ceangailte le PostgreSQL',
    savedTitle: 'PostgreSQL sábháilte',
    readOnlyReplica: 'Is macasamhail inléite amháin é an bunachar sonraí seo.',
    readOnlyAccount:
      'Ní thacaíonn na táblaí roghnaithe le hathruithe ró leis an gcuntas bunachair sonraí seo.',
    tablesReachOne: 'A bhfuil rochtain aige air · {count} tábla',
    tablesReachMany: 'A bhfuil rochtain aige air · {count} táblaí',
    tlsReplica: 'Macasamhail inléite amháin',
    tlsAccount: 'Cuntas inléite amháin',
    tlsChecked: 'Seiceáladh ceadanna an bhunachair sonraí',
    tablesSelected: 'Roghnaíodh {count} as {total} tábla',
    tablesInSchema: '{count} / {total} tábla',
    reconnect: 'Athcheangail',
  },
  it: {
    manageTitle: 'Gestisci PostgreSQL',
    connectTitle: 'Connetti PostgreSQL',
    chooseTablesTitle: 'Scegli le tabelle',
    chooseAccessTitle: "Scegli l'accesso al database",
    connectedTitle: 'PostgreSQL connesso',
    savedTitle: 'PostgreSQL salvato',
    readOnlyReplica: 'Questo database è una replica di sola lettura.',
    readOnlyAccount:
      'Le tabelle selezionate non supportano modifiche alle righe con questo account del database.',
    tablesReachOne: 'Ambito di accesso · {count} tabella',
    tablesReachMany: 'Ambito di accesso · {count} tabelle',
    tlsReplica: 'Replica di sola lettura',
    tlsAccount: 'Account di sola lettura',
    tlsChecked: 'Autorizzazioni del database verificate',
    tablesSelected: '{count} di {total} tabelle selezionate',
    tablesInSchema: '{count} / {total} tabelle',
    reconnect: 'Riconnetti',
  },
  lv: {
    manageTitle: 'Pārvaldīt PostgreSQL',
    connectTitle: 'Savienot PostgreSQL',
    chooseTablesTitle: 'Izvēlēties tabulas',
    chooseAccessTitle: 'Izvēlēties piekļuvi datubāzei',
    connectedTitle: 'PostgreSQL savienots',
    savedTitle: 'PostgreSQL saglabāts',
    readOnlyReplica: 'Šī datubāze ir tikai lasāma replikas datubāze.',
    readOnlyAccount: 'Atlasītajās tabulās ar šo datubāzes kontu nevar veikt rindu izmaiņas.',
    tablesReachOne: 'Pieejams · {count} tabula',
    tablesReachMany: 'Pieejams · {count} tabulas',
    tlsReplica: 'Tikai lasāma replika',
    tlsAccount: 'Tikai lasāms konts',
    tlsChecked: 'Datubāzes atļaujas pārbaudītas',
    tablesSelected: 'Atlasītas {count} no {total} tabulām',
    tablesInSchema: '{count} / {total} tabulas',
    reconnect: 'Atkārtoti izveidot savienojumu',
  },
  lt: {
    manageTitle: 'Tvarkyti PostgreSQL',
    connectTitle: 'Prijungti PostgreSQL',
    chooseTablesTitle: 'Pasirinkti lenteles',
    chooseAccessTitle: 'Pasirinkti prieigą prie duomenų bazės',
    connectedTitle: 'PostgreSQL prijungtas',
    savedTitle: 'PostgreSQL išsaugotas',
    readOnlyReplica: 'Ši duomenų bazė yra tik skaitoma replika.',
    readOnlyAccount:
      'Pasirinktos lentelės neleidžia keisti eilučių naudojant šią duomenų bazės paskyrą.',
    tablesReachOne: 'Pasiekiama · {count} lentelė',
    tablesReachMany: 'Pasiekiama · {count} lentelių',
    tlsReplica: 'Tik skaitoma replika',
    tlsAccount: 'Tik skaitoma paskyra',
    tlsChecked: 'Duomenų bazės leidimai patikrinti',
    tablesSelected: 'Pasirinkta {count} iš {total} lentelių',
    tablesInSchema: '{count} / {total} lentelių',
    reconnect: 'Prisijungti iš naujo',
  },
  mt: {
    manageTitle: 'Immaniġġja PostgreSQL',
    connectTitle: "Ikkonnettja ma' PostgreSQL",
    chooseTablesTitle: 'Agħżel it-tabelli',
    chooseAccessTitle: 'Agħżel l-aċċess għad-database',
    connectedTitle: 'PostgreSQL konness',
    savedTitle: 'PostgreSQL issejvjat',
    readOnlyReplica: 'Din id-database hija replika għall-qari biss.',
    readOnlyAccount:
      "It-tabelli magħżula ma jappoġġjawx bidliet fir-ringieli b'dan il-kont tad-database.",
    tablesReachOne: "Dak li jista' jaċċessa · {count} tabella",
    tablesReachMany: "Dak li jista' jaċċessa · {count} tabelli",
    tlsReplica: 'Replika għall-qari biss',
    tlsAccount: 'Kont għall-qari biss',
    tlsChecked: 'Il-permessi tad-database ġew ivverifikati',
    tablesSelected: '{count} minn {total} tabelli magħżula',
    tablesInSchema: '{count} / {total} tabelli',
    reconnect: 'Ikkonnettja mill-ġdid',
  },
  pl: {
    manageTitle: 'Zarządzaj PostgreSQL',
    connectTitle: 'Połącz z PostgreSQL',
    chooseTablesTitle: 'Wybierz tabele',
    chooseAccessTitle: 'Wybierz dostęp do bazy danych',
    connectedTitle: 'Połączono z PostgreSQL',
    savedTitle: 'Zapisano PostgreSQL',
    readOnlyReplica: 'Ta baza danych jest repliką tylko do odczytu.',
    readOnlyAccount:
      'Wybrane tabele nie obsługują zmian wierszy przy użyciu tego konta bazy danych.',
    tablesReachOne: 'Dostępne · {count} tabela',
    tablesReachMany: 'Dostępne · {count} tabel',
    tlsReplica: 'Replika tylko do odczytu',
    tlsAccount: 'Konto tylko do odczytu',
    tlsChecked: 'Uprawnienia do bazy danych zostały sprawdzone',
    tablesSelected: 'Wybrano {count} z {total} tabel',
    tablesInSchema: '{count} / {total} tabel',
    reconnect: 'Połącz ponownie',
  },
  pt: {
    manageTitle: 'Gerenciar PostgreSQL',
    connectTitle: 'Conectar PostgreSQL',
    chooseTablesTitle: 'Escolher tabelas',
    chooseAccessTitle: 'Escolher acesso ao banco de dados',
    connectedTitle: 'PostgreSQL conectado',
    savedTitle: 'PostgreSQL salvo',
    readOnlyReplica: 'Este banco de dados é uma réplica somente leitura.',
    readOnlyAccount:
      'As tabelas selecionadas não permitem alterações de linhas com esta conta do banco de dados.',
    tablesReachOne: 'O que pode acessar · {count} tabela',
    tablesReachMany: 'O que pode acessar · {count} tabelas',
    tlsReplica: 'Réplica somente leitura',
    tlsAccount: 'Conta somente leitura',
    tlsChecked: 'Permissões do banco de dados verificadas',
    tablesSelected: '{count} de {total} tabelas selecionadas',
    tablesInSchema: '{count} / {total} tabelas',
    reconnect: 'Reconectar',
  },
  ro: {
    manageTitle: 'Gestionare PostgreSQL',
    connectTitle: 'Conectare la PostgreSQL',
    chooseTablesTitle: 'Alegeți tabelele',
    chooseAccessTitle: 'Alegeți accesul la baza de date',
    connectedTitle: 'PostgreSQL conectat',
    savedTitle: 'PostgreSQL salvat',
    readOnlyReplica: 'Această bază de date este o replică doar pentru citire.',
    readOnlyAccount:
      'Tabelele selectate nu permit modificarea rândurilor cu acest cont de bază de date.',
    tablesReachOne: 'Ce poate accesa · {count} tabel',
    tablesReachMany: 'Ce poate accesa · {count} tabele',
    tlsReplica: 'Replică doar pentru citire',
    tlsAccount: 'Cont doar pentru citire',
    tlsChecked: 'Permisiunile bazei de date au fost verificate',
    tablesSelected: '{count} din {total} tabele selectate',
    tablesInSchema: '{count} / {total} tabele',
    reconnect: 'Reconectare',
  },
  sk: {
    manageTitle: 'Spravovať PostgreSQL',
    connectTitle: 'Pripojiť PostgreSQL',
    chooseTablesTitle: 'Vybrať tabuľky',
    chooseAccessTitle: 'Vybrať prístup k databáze',
    connectedTitle: 'PostgreSQL je pripojené',
    savedTitle: 'PostgreSQL je uložené',
    readOnlyReplica: 'Táto databáza je replika len na čítanie.',
    readOnlyAccount:
      'Vybrané tabuľky nepodporujú zmeny riadkov pri použití tohto databázového účtu.',
    tablesReachOne: 'Čo je dostupné · {count} tabuľka',
    tablesReachMany: 'Čo je dostupné · {count} tabuliek',
    tlsReplica: 'Replika len na čítanie',
    tlsAccount: 'Účet len na čítanie',
    tlsChecked: 'Povolenia databázy boli skontrolované',
    tablesSelected: 'Vybrané tabuľky: {count} z {total}',
    tablesInSchema: '{count} / {total} tabuliek',
    reconnect: 'Znova pripojiť',
  },
  sl: {
    manageTitle: 'Upravljanje PostgreSQL',
    connectTitle: 'Poveži PostgreSQL',
    chooseTablesTitle: 'Izberite tabele',
    chooseAccessTitle: 'Izberite dostop do zbirke podatkov',
    connectedTitle: 'PostgreSQL je povezan',
    savedTitle: 'PostgreSQL je shranjen',
    readOnlyReplica: 'Ta zbirka podatkov je replika samo za branje.',
    readOnlyAccount:
      'Izbrane tabele s tem računom zbirke podatkov ne podpirajo spreminjanja vrstic.',
    tablesReachOne: 'Dosegljivo · {count} tabela',
    tablesReachMany: 'Dosegljivo · {count} tabel',
    tlsReplica: 'Replika samo za branje',
    tlsAccount: 'Račun samo za branje',
    tlsChecked: 'Dovoljenja za zbirko podatkov so preverjena',
    tablesSelected: 'Izbranih je {count} od {total} tabel',
    tablesInSchema: '{count} / {total} tabel',
    reconnect: 'Znova poveži',
  },
  es: {
    manageTitle: 'Administrar PostgreSQL',
    connectTitle: 'Conectar PostgreSQL',
    chooseTablesTitle: 'Elegir tablas',
    chooseAccessTitle: 'Elegir acceso a la base de datos',
    connectedTitle: 'PostgreSQL conectado',
    savedTitle: 'PostgreSQL guardado',
    readOnlyReplica: 'Esta base de datos es una réplica de solo lectura.',
    readOnlyAccount:
      'Las tablas seleccionadas no admiten cambios en las filas con esta cuenta de base de datos.',
    tablesReachOne: 'Alcance disponible · {count} tabla',
    tablesReachMany: 'Alcance disponible · {count} tablas',
    tlsReplica: 'Réplica de solo lectura',
    tlsAccount: 'Cuenta de solo lectura',
    tlsChecked: 'Permisos de la base de datos comprobados',
    tablesSelected: '{count} de {total} tablas seleccionadas',
    tablesInSchema: '{count} / {total} tablas',
    reconnect: 'Volver a conectar',
  },
  sv: {
    manageTitle: 'Hantera PostgreSQL',
    connectTitle: 'Anslut PostgreSQL',
    chooseTablesTitle: 'Välj tabeller',
    chooseAccessTitle: 'Välj databasåtkomst',
    connectedTitle: 'PostgreSQL anslutet',
    savedTitle: 'PostgreSQL sparat',
    readOnlyReplica: 'Den här databasen är en skrivskyddad replika.',
    readOnlyAccount: 'De valda tabellerna stöder inte ändringar av rader med det här dataskontot.',
    tablesReachOne: 'Åtkomliga tabeller · {count} tabell',
    tablesReachMany: 'Åtkomliga tabeller · {count} tabeller',
    tlsReplica: 'Skrivskyddad replika',
    tlsAccount: 'Skrivskyddat konto',
    tlsChecked: 'Databasbehörigheter kontrollerade',
    tablesSelected: '{count} av {total} tabeller valda',
    tablesInSchema: '{count} / {total} tabeller',
    reconnect: 'Anslut igen',
  },
} as const satisfies NamespaceCatalog<NativePostgresKey>;

export default catalog;
